import { useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';

/**
 * Keep the keyboard inside a modal, and put it there to begin with.
 *
 * `aria-modal="true"` is a promise: it tells assistive technology that
 * everything outside this element is inert. Nothing enforces it. A dialog that
 * makes the promise and then leaves focus on the page behind it strands a
 * screen reader user on a page they have just been told is unavailable, and it
 * lets a keyboard player tab onto cards the dialog is covering.
 *
 * Every modal surface (`Sheet`, `Cover`, `ActionMenu`, the Classic reviews and
 * the hand-off cover) needs the same mount effect, Tab wrap, `focusableIn` and
 * selector, so this is their one home rather than a copy in each.
 *
 * Escape is optional and Tab is not. Trapping Tab is unconditionally right for
 * anything wearing `aria-modal`. Closing on Escape is a decision about the
 * dialog: a drawer you put away closes, a review panel closes, a confirm cancels
 * — but three of the game's six covers have no way out on purpose, a handoff you
 * have to acknowledge and a game that is over. Those pass no `onEscape` and get
 * no dismissal invented for them.
 */

const FOCUSABLE =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

function focusableIn(root: HTMLElement | null): readonly HTMLElement[] {
  if (root === null) return [];
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)];
}

/**
 * The one page that has more than one modal open at once.
 *
 * A cover is `position: fixed`, so in the game taking focus scrolls nothing.
 * The showcase renders six of them at a time inside paint-contained wells,
 * which makes each one a real box a long way down a very long page — and the
 * last to mount drags the document to itself on load. Only that page needs
 * the guard, so only that page gets it; everywhere else focus keeps the
 * browser's ordinary scroll-into-view.
 */
const SHOWCASE_PATH = '/dev/lookbook';

function inShowcase(): boolean {
  return window.location.pathname === SHOWCASE_PATH;
}

export interface FocusTrap<T extends HTMLElement> {
  /** Put this on the element that carries `aria-modal` and `tabIndex={-1}`. */
  readonly ref: RefObject<T | null>;
  /** And this. It handles Tab, and Escape when `onEscape` was given. */
  readonly onKeyDown: (event: KeyboardEvent<T>) => void;
}

export function useFocusTrap<T extends HTMLElement>(onEscape?: () => void): FocusTrap<T> {
  const ref = useRef<T>(null);

  /*
   * The container itself takes focus, not the first control in it. That is what
   * makes a screen reader read the dialog's accessible name before its buttons,
   * and it is why every caller needs `tabIndex={-1}` and `focus:outline-none` —
   * a focusable container without the second one draws a ring around the whole
   * screen.
   */
  useEffect(() => {
    ref.current?.focus({ preventScroll: inShowcase() });
  }, []);

  const onKeyDown = (event: KeyboardEvent<T>): void => {
    // A nested modal already wrapped this key within its own controls.
    if (event.defaultPrevented) return;
    if (event.key === 'Escape' && onEscape !== undefined) {
      event.stopPropagation();
      onEscape();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = focusableIn(ref.current);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (first === undefined || last === undefined) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === ref.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return { ref, onKeyDown };
}
